import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';

/**
 * Media uploads (epic ②c C3). Free-tier guardrails are non-negotiable:
 * every image is client-side compressed (canvas resize + JPEG re-encode)
 * before upload, with hard input caps — egress is the one quota this app
 * has ever blown, and uncompressed media multiplies it.
 *
 * Images ride the existing PUBLIC `social-media` bucket (own-folder write
 * RLS, public CDN-cached reads). DM files ride the PRIVATE `dm-files`
 * bucket — media_url stores the object PATH and clients mint short-lived
 * signed URLs (access follows the conversation's RLS).
 * ⚠️⚠️ DM PHOTOS ARE PRIVATE TOO (2026-10-16, R2 plan phase 1): they used to go to
 * the PUBLIC bucket, so a private message's photo was public to anyone holding the
 * link. They now ride `dm-files` like DM files (`uploadDmImage`), and `uploadImage`
 * no longer accepts the 'dm' purpose at all.
 */

export type ImagePurpose = 'post' | 'story' | 'avatar' | 'banner' | 'dm' | 'meal';

/** Hard input caps — anything bigger is rejected before any work happens. */
export const IMAGE_INPUT_MAX_BYTES = 15 * 1024 * 1024;
export const DM_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** Longest-edge target per purpose (avatars stay small — they render at ≤80px). */
const MAX_DIMENSION: Record<ImagePurpose, number> = {
  post: 1600,
  story: 1600,
  dm: 1600,
  banner: 1600,
  avatar: 512,
  meal: 800, // shown as thumbnails and a small preview — never needs more
};

const JPEG_QUALITY = 0.82;

// animated GIFs die under canvas re-encode — small ones pass through as-is
const GIF_PASSTHROUGH_MAX_BYTES = 2 * 1024 * 1024;

/** Returns an error message, or null when the image input is acceptable. */
export function validateImageInput(file: { type: string; size: number }): string | null {
  if (!file.type.startsWith('image/')) return 'Only images can be attached here.';
  if (file.size > IMAGE_INPUT_MAX_BYTES) {
    return `That image is too large (max ${Math.round(IMAGE_INPUT_MAX_BYTES / 1024 / 1024)}MB).`;
  }
  return null;
}

/** Returns an error message, or null when the DM file input is acceptable. */
export function validateDmFileInput(file: { size: number }): string | null {
  if (file.size > DM_FILE_MAX_BYTES) {
    return `That file is too large (max ${Math.round(DM_FILE_MAX_BYTES / 1024 / 1024)}MB).`;
  }
  return null;
}

/** Small GIFs skip compression to keep their animation. */
export function shouldBypassCompression(file: { type: string; size: number }): boolean {
  return file.type === 'image/gif' && file.size <= GIF_PASSTHROUGH_MAX_BYTES;
}

/** `<uid>/<purpose>-<uuid>.<ext>` — the folder name IS the RLS boundary. */
export function buildObjectPath(userId: string, purpose: ImagePurpose, ext: string): string {
  return `${userId}/${purpose}-${crypto.randomUUID()}.${ext}`;
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

/** Resize to the purpose's longest edge and re-encode as JPEG. */
export async function compressImage(file: File, purpose: ImagePurpose): Promise<Blob> {
  const img = await loadImage(file);

  const maxDim = MAX_DIMENSION[purpose];
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image.');
  // transparency flattens to white rather than black under JPEG
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) throw new Error('Could not process that image.');
  return blob;
}

/**
 * Validate → compress → upload to the public social-media bucket.
 * Returns the public URL to store in media_url / avatar_url / banner_url.
 */
/** The purposes that may go to the PUBLIC bucket — never a DM, never a meal photo. */
export type PublicImagePurpose = Exclude<ImagePurpose, 'dm' | 'meal'>;

export async function uploadImage(file: File, purpose: PublicImagePurpose): Promise<string> {
  const inputError = validateImageInput(file);
  if (inputError) throw new Error(inputError);

  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Not signed in.');

  const bypass = shouldBypassCompression(file);
  const blob = bypass ? file : await compressImage(file, purpose);
  const path = buildObjectPath(user.id, purpose, bypass ? 'gif' : 'jpg');

  const { error } = await supabase.storage
    .from('social-media')
    .upload(path, blob, {
      contentType: bypass ? 'image/gif' : 'image/jpeg',
      cacheControl: '31536000', // immutable content-addressed-ish paths — cache hard
    });
  if (error) throw error;

  const { data } = supabase.storage.from('social-media').getPublicUrl(path);
  return data.publicUrl;
}

/**
 * A DM photo, PRIVATE: compressed like any image, uploaded to `dm-files` under the
 * sender's folder, and returned as the object PATH (stored in `media_url`). Readers
 * mint a short-lived link with `signedDmFileUrl` — the bucket's RLS lets the sender
 * and the conversation's recipient read it, nobody else.
 * ⚠️ Always re-encoded to JPEG: `dm-files` accepts jpeg/png/webp, not gif, so an
 * animated GIF arrives as a still.
 */
export async function uploadDmImage(file: File): Promise<string> {
  const inputError = validateImageInput(file);
  if (inputError) throw new Error(inputError);

  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Not signed in.');

  const blob = await compressImage(file, 'dm');
  const path = buildObjectPath(user.id, 'dm', 'jpg');

  const { error } = await supabase.storage
    .from('dm-files')
    .upload(path, blob, { contentType: 'image/jpeg' });
  if (error) throw error;
  return path;
}

/** A DM photo stored before 2026-10-16 is a full public URL; since then, a private path. */
export const isPublicMediaUrl = (value: string) => /^https?:\/\//i.test(value);

export interface UploadedDmFile {
  /** Object PATH in the private dm-files bucket (stored in media_url). */
  path: string;
  fileName: string;
  fileSize: number;
}

/** Upload a DM attachment to the private bucket (original bytes, no re-encode). */
export async function uploadDmFile(file: File): Promise<UploadedDmFile> {
  const inputError = validateDmFileInput(file);
  if (inputError) throw new Error(inputError);

  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Not signed in.');

  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'bin';
  const path = `${user.id}/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('dm-files')
    .upload(path, file, { contentType: file.type || 'application/octet-stream' });
  if (error) throw error;

  return { path, fileName: file.name, fileSize: file.size };
}

// ─── Inbox media (private, owner-only) ──────────────────────────────────────
// Migration 0236 created the `inbox-media` bucket. It is PRIVATE and owner-only on
// every verb — inbox attachments are voice notes about the user's own life, so
// unlike `social-media` (public) or `dm-files` (readable by DM counterparties)
// nobody but the owner may ever read them. Paths are stored on the item; playback
// mints a short-lived signed URL.
const INBOX_MEDIA_BUCKET = 'inbox-media';
const INBOX_MEDIA_MAX_BYTES = 10 * 1024 * 1024;

/** Returns an error message, or null when the inbox attachment is acceptable. */
export function validateInboxMediaInput(file: { size: number }): string | null {
  if (file.size > INBOX_MEDIA_MAX_BYTES) {
    return `That file is too large (max ${Math.round(INBOX_MEDIA_MAX_BYTES / 1024 / 1024)}MB).`;
  }
  return null;
}

/** Upload an inbox attachment (voice note, image) to the private bucket. */
export async function uploadInboxMedia(file: File | Blob, ext: string): Promise<string> {
  const inputError = validateInboxMediaInput(file);
  if (inputError) throw new Error(inputError);

  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Not signed in.');

  const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from(INBOX_MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream' });
  if (error) throw error;
  return path;
}

/** Short-lived signed URL for a private inbox attachment (RLS gates minting). */
export async function signedInboxMediaUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(INBOX_MEDIA_BUCKET)
    .createSignedUrl(path, 60 * 60);
  if (error) {
    console.error('Error signing inbox media URL:', error);
    return null;
  }
  return data.signedUrl;
}

/** Remove an inbox attachment from storage (own-folder RLS enforces ownership). */
export async function deleteInboxMedia(path: string): Promise<void> {
  const { error } = await supabase.storage.from(INBOX_MEDIA_BUCKET).remove([path]);
  if (error) console.error('Error deleting inbox media:', error);
}

/** Short-lived signed URL for a private DM file (RLS gates who can mint it). */
export async function signedDmFileUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('dm-files')
    .createSignedUrl(path, 60 * 10);
  if (error) {
    console.error('Error signing DM file URL:', error);
    return null;
  }
  return data.signedUrl;
}

// ─── Meal photos (private, owner-only) ───────────────────────────────────────
// Migration 0301 created the `meal-photos` bucket: PRIVATE, owner-only on every verb
// (a meal photo is the person's own food — nobody else reads it). The meal stores the
// object PATH (`nutrition_meals.photo_path`); showing it mints a short-lived signed link.
// Photos are shrunk to 800 px JPEG first — they show as thumbnails.
const MEAL_PHOTOS_BUCKET = 'meal-photos';
const MEAL_PHOTO_LINK_SECONDS = 60 * 60;

/** Validate → shrink → upload to the owner's folder. Returns the object PATH. */
export async function uploadMealPhoto(file: File): Promise<string> {
  const inputError = validateImageInput(file);
  if (inputError) throw new Error(inputError);
  const { user } = await syncManager.safeAuthCheck();
  if (!user) throw new Error('Not signed in.');
  const blob = await compressImage(file, 'meal');
  const path = `${user.id}/${crypto.randomUUID()}.jpg`;
  // A path is never reused (a new photo is a new upload), so its bytes never change: cache hard.
  const { error } = await supabase.storage.from(MEAL_PHOTOS_BUCKET).upload(path, blob, { contentType: 'image/jpeg', cacheControl: '31536000' });
  if (error) throw error;
  return path;
}

/** Signed links already minted, so a list doesn't re-sign every photo on every render. */
const mealPhotoLinks = new Map<string, { url: string; until: number }>();
/** Links being minted: every photo on screen asking at once makes ONE request (measured 2026-10-20: 8 for one photo). */
const mealPhotoSigning = new Map<string, Promise<string | null>>();
export const forgetMealPhotoLinks = () => { mealPhotoLinks.clear(); mealPhotoSigning.clear(); };
const freshLink = (path: string) => { const hit = mealPhotoLinks.get(path); return hit && hit.until - Date.now() > 5 * 60 * 1000 ? hit.url : null; };

/** A short-lived link to a meal photo (RLS: only its owner can mint one), reused while it has 5+ minutes left. */
export function signedMealPhotoUrl(path: string): Promise<string | null> {
  const hit = freshLink(path);
  if (hit) return Promise.resolve(hit);
  const running = mealPhotoSigning.get(path);
  if (running) return running;
  const p = (async () => {
    const { data, error } = await supabase.storage.from(MEAL_PHOTOS_BUCKET).createSignedUrl(path, MEAL_PHOTO_LINK_SECONDS);
    if (error || !data?.signedUrl) {
      console.error('Error signing a meal photo link:', error);
      return null;
    }
    mealPhotoLinks.set(path, { url: data.signedUrl, until: Date.now() + MEAL_PHOTO_LINK_SECONDS * 1000 });
    return data.signedUrl;
  })().finally(() => { if (mealPhotoSigning.get(path) === p) mealPhotoSigning.delete(path); });
  mealPhotoSigning.set(path, p);
  return p;
}

/** Links for several photos in ONE request (Meals warming its list); paths with a fresh link are skipped. Best effort. */
export async function signMealPhotoUrls(paths: ReadonlyArray<string>): Promise<void> {
  const need = [...new Set(paths)].filter(p => !freshLink(p) && !mealPhotoSigning.has(p));
  if (!need.length) return;
  const { data, error } = await supabase.storage.from(MEAL_PHOTOS_BUCKET).createSignedUrls(need, MEAL_PHOTO_LINK_SECONDS);
  if (error || !data) {
    console.error('Error signing meal photo links:', error);
    return;
  }
  const until = Date.now() + MEAL_PHOTO_LINK_SECONDS * 1000;
  for (const d of data) if (d.path && d.signedUrl && !d.error) mealPhotoLinks.set(d.path, { url: d.signedUrl, until });
}

/** Told when a photo is removed, so a copy kept to show it can go too (`mealPhotoCache`). */
const mealPhotoRemoved = new Set<(path: string) => void>();
export function onMealPhotoRemoved(fn: (path: string) => void): () => void {
  mealPhotoRemoved.add(fn);
  return () => { mealPhotoRemoved.delete(fn); };
}

/** Remove a meal photo from storage (own-folder RLS enforces ownership). Best effort. */
export async function deleteMealPhoto(path: string): Promise<void> {
  mealPhotoLinks.delete(path);
  for (const fn of mealPhotoRemoved) { try { fn(path); } catch { /* a listener never stops the delete */ } }
  const { error } = await supabase.storage.from(MEAL_PHOTOS_BUCKET).remove([path]);
  if (error) console.error('Error deleting a meal photo:', error);
}

